import { Cluster, Vector as VectorSource } from 'ol/source';
import { Geometry, Point } from 'ol/geom';
import { Fill, Icon, Stroke, Style, Text} from 'ol/style';
import { Feature, MapBrowserEvent, Overlay } from 'ol';
import { SvgIcons } from '../../local_modules/voyage-svg-icons/src/voyage-svg-icons';
import { NearestPortPosition } from '../models/nearest-port-model';
import RenderFeature from 'ol/render/Feature';
import { FeatureLike } from 'ol/Feature';
import { Map as MGMap } from '@meteogroup/map-layers';
import { VoyMapTerminalTooltip } from '../../local_modules/voy-map-tooltips/src/voy-map-terminal-tooltip';
import { fromLonLat } from 'ol/proj';
import '../../local_modules/voy-map-tooltips/src/voy-map-terminal-tooltip';
import { VoyageShoreContextModel } from '../models/voyage-shore-context-model';
import { FiSubView, UiStateModel, UiStateModelParam } from '../models/ui-state-model';
import VectorImageLayer from 'ol/layer/VectorImage';
import { TerminalsModel } from '../models/terminals-model';
import { TerminalInfo } from '../models/types/terminals-model.types';
import { VoyageLayersNames } from './types/voyage-layers.types';
import { ShoreContextEvent } from '../models/types/voyage-shore-context-model.types';

const _PROJECTION = (<any>window)["appenv"].projection;

const textFill = new Fill({color: '#fff'});
const textStroke = new Stroke({color: 'rgba(0, 0, 0, 0.6)', width: 3});
const terminalIcon = new Icon({
    src: 'data:image/svg+xml,' + encodeURIComponent(SvgIcons.customPinTerminal.strings[0]),
    size: [24, 24],
    anchor: [0.5, 1],
});
const clusterIcon = new Icon({
    src: 'data:image/svg+xml,' + encodeURIComponent(SvgIcons.customPinCluster.strings[0]),
    size: [24, 24],
    anchor: [0.5, 1],
});
const textStyle = new Text({
    text: 'id',
    font: '10px MaerskText',
    offsetY: -12,
    fill: textFill,
    stroke: textStroke,
});
const highlightTextStyle = new Text({
    text: 'id',
    font: '20px MaerskText',
    offsetY: -27,
    fill: textFill,
    stroke: textStroke,
});
const terminalStyle = new Style({
    image: terminalIcon,
});
const clusterStyle = new Style({
    image: clusterIcon,
    text: textStyle,
});

export class PortAndTerminalLayer extends EventTarget
{
    private _layer : VectorImageLayer<VectorSource<Geometry>> | null = null;
    private _highlights: NearestPortPosition[]|null = [];
    private _map: MGMap| null = null;
    private _tooltipElement: VoyMapTerminalTooltip | null = null;
    private _tooltipOverlay: Overlay | null = null;
    private _isPortView = UiStateModel!.getValue(UiStateModelParam.FISubView) === FiSubView.NearestPort;

    constructor()
    {
        super();
        this._styleFunction = this._styleFunction.bind(this);
        this._groupTerminalsByCity = this._groupTerminalsByCity.bind(this);
        this._plotTerminals = this._plotTerminals.bind(this);
        this._setVisibility = this._setVisibility.bind(this);
    }

    get layers(): VectorImageLayer<VectorSource<Geometry>>[]
    {
        return this._layers;
    }

    async initialize(): Promise<void>
    {
        this._vectorSource = new VectorSource();
        this._layer = new VectorImageLayer({
            source: new Cluster({
                source: this._vectorSource,                
            }), 
            zIndex: 35,
            visible: true,
            style: this._styleFunction
        });
        this._layers.push(this._layer);
        this._plotTerminals();
        VoyageShoreContextModel!.addEventListener(ShoreContextEvent.TerminalsChange, this._plotTerminals);
        UiStateModel!.addEventListener(`${UiStateModelParam.FISubView}-change`, this._setVisibility);
        UiStateModel!.addEventListener(`${UiStateModelParam.ListView}-change`, this._setVisibility);
    }

    updatePortHighlights(details: NearestPortPosition[]) {
        this._highlights = details;
        this._isPortView = UiStateModel!.getValue(UiStateModelParam.FISubView) === FiSubView.NearestPort;
        this._layer?.changed();
    }

    private _styleFunction(feature: FeatureLike): Style {
        const subfeatures = <Feature<Geometry>[]>feature.get('features');

        const mustHighlight = subfeatures.reduce((p, c) => {
            const properties = <TerminalInfo>c.get('properties');
            return this._highlights?.findIndex((h) => h.name === properties.cityId) !== -1 || p;
        }, false);

        if (subfeatures.length > 1) {
            if (mustHighlight) {
                clusterStyle.setText(highlightTextStyle);
                clusterIcon.setScale(2);
                highlightTextStyle.setText(subfeatures.length.toString());
                clusterIcon.setOpacity(1)
            } else {
                clusterStyle.setText(textStyle);
                clusterIcon.setScale(1);
                textStyle.setText(subfeatures.length.toString());
                if (this._isPortView) {
                    clusterIcon.setOpacity(0)
                    highlightTextStyle.setText("")
                }
            }
            return clusterStyle;
        } else {
            if (mustHighlight) {
                terminalIcon.setScale(2);
                terminalIcon.setOpacity(1)
            } else {
                terminalIcon.setScale(1);
                if (this._isPortView) {
                    terminalIcon.setOpacity(0)
                }
            }
            return terminalStyle;
        }
    }

    private _clear()
    {
        try {
            this._vectorSource!.clear();
        } catch (error) {
            console.warn(error);
        }
    }

    async enable() {
        this._clear();
        await this._plotTerminals();
        this._layer!.setVisible(true)
        TerminalsModel!.addEventListener('terminals-updated', this._plotTerminals);
    }

    async disable() {
        this._layer!.setVisible(false);
        TerminalsModel!.removeEventListener('terminals-updated', this._plotTerminals);
        this._clear();
    }

    private _layers: VectorImageLayer<VectorSource<Geometry>>[] = [];
    private _vectorSource: VectorSource<Geometry>|null = null;

    private async _plotTerminals()
    {
        const _terminalFeatures: Feature<Point>[] = VoyageShoreContextModel!.terminals.map(terminal => {
            const feature = new Feature({
                geometry: new Point([terminal.longitude, terminal.latitude]),
                properties: terminal,
                name: terminal.locationId,
                layer: 'Terminals',
            });
            feature.set('layer', VoyageLayersNames.TerminalPortLayer)
            feature.getGeometry()?.transform('EPSG:4326', _PROJECTION);

            return feature
        })

        this._vectorSource?.clear();
        this._vectorSource?.addFeatures(_terminalFeatures);
    }

    assignMap(map: MGMap) {
        this._map = map;
        this._createPortPinTooltip();

        if (this._map) {
            this._map.on('moveend', () => this._setVisibility());
        }
    }

    private _setVisibility() {
        const isPortView = UiStateModel!.getValue(UiStateModelParam.FISubView) === FiSubView.NearestPort;
        const zoomLevel = this._map!.getView().getZoom();
        const isVisible = this._layer!.getVisible();
        const showPortLayer = zoomLevel && zoomLevel >= 5 || isPortView;
        if (showPortLayer && !isVisible) {
            this.enable();
        }
        if (!showPortLayer && isVisible) {
            this.disable();
        }
    }

    private _createPortPinTooltip() {
        this._tooltipElement = <VoyMapTerminalTooltip>window.document.createElement('voy-map-terminal-tooltip');
       
        this._tooltipOverlay = new Overlay({
            position: fromLonLat([0, 0], _PROJECTION),
            positioning: 'bottom-center',
            element:  this._tooltipElement,
            stopEvent: false,
            offset: [0, -18]
        });
        this._map?.addOverlay(this._tooltipOverlay);
    }

    private _groupTerminalsByCity(features: RenderFeature[] | Feature<Geometry>[]) {
        let grouppedTerminalsByCity = [];
        features.sort(( a: FeatureLike, b: FeatureLike ) => {
            let aProps = a.get('properties');
            let bProps = b.get('properties');
            return aProps.cityId.localeCompare(bProps.cityId);
        });

        for ( let subfeature of features ) {
            let props = subfeature.get('properties');
            if (grouppedTerminalsByCity.length === 0 || grouppedTerminalsByCity[grouppedTerminalsByCity.length-1].city !== props.city) {
                grouppedTerminalsByCity.push({city: props.city, cityId: props.cityId, terminals: [props]});
            } else {
                grouppedTerminalsByCity[grouppedTerminalsByCity.length-1].terminals.push(props);
            }
        }

        return grouppedTerminalsByCity;
    }

    
};
